import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, UserPlus, Loader2, Copy, RefreshCw, Trash2, ShieldCheck, UserMinus, UserCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function RoleBadge({ role }) {
  const cls = role === "owner"
    ? "bg-accent/10 text-accent border-accent/40"
    : "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span className={`inline-flex items-center border text-[10px] uppercase tracking-[0.2em] font-bold rounded px-2 py-0.5 mono ${cls}`}>
      {role === "owner" ? "Owner" : "Admin"}
    </span>
  );
}

export default function Team() {
  const { manager } = useAuth();
  const amOwner = manager?.role === "owner";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [devInvite, setDevInvite] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!amOwner) return;
    setSubmitting(true);
    setDevInvite(null);
    try {
      const { data: inv } = await api.post("/team/invite", { email: form.email.trim(), name: form.name.trim() });
      setForm({ email: "", name: "" });
      if (inv.dev_token) setDevInvite(inv);
      toast.success(`Invite sent to ${inv.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (id) => {
    try {
      const { data: inv } = await api.post(`/team/invite/${id}/resend`);
      if (inv.dev_token) setDevInvite(inv);
      toast.success("Invite re-sent");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const cancelInvite = async (id) => {
    try { await api.delete(`/team/invite/${id}`); toast.success("Invite cancelled"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toggleActive = async (m) => {
    try { await api.put(`/team/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Teammate deactivated" : "Teammate reactivated"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/team/${id}`); toast.success("Teammate removed"); setConfirmDel(null); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const devLink = devInvite?.dev_token ? `${window.location.origin}/team/accept?token=${devInvite.dev_token}` : "";

  if (loading || !data) {
    return <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading team…</div>;
  }

  const { members, pending_invites: pending, plan_code, seat_limit, current_seat_count } = data;
  // Phase O — 2-state banner: amber when at cap, red when over cap.
  // The old "basic = no admins" special-case is gone: basic now allows
  // 1 admin (owner + 1 = 2 total). Trial also caps at 2 in Phase O.
  const atCap = seat_limit !== null && current_seat_count === seat_limit;
  const overCap = seat_limit !== null && current_seat_count > seat_limit;

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-10" data-testid="team-page">
      <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// Team</div>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Manage your teammates</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Owners can invite admins, deactivate accounts, or remove teammates. Admins can manage day-to-day operations but not billing or settings.
      </p>

      {/* Plan / seat header */}
      <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
        <span className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Plan</span>
        <span className="mono text-[11px] uppercase tracking-[0.2em] bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-bold">{plan_code}</span>
        {seat_limit !== null ? (
          <span className="mono text-[11px] text-muted-foreground">· {current_seat_count} of {seat_limit} seats used</span>
        ) : (
          <span className="mono text-[11px] text-muted-foreground">· unlimited seats</span>
        )}
      </div>

      {/* Members list */}
      <h2 className="mt-8 text-lg font-extrabold tracking-tight">Active members</h2>
      <div className="mt-3 border border-border rounded-[8px] overflow-hidden" data-testid="team-members-list">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-border bg-card"
               data-testid={`team-member-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold truncate">{m.name}</span>
                <RoleBadge role={m.role} />
                {!m.is_active && <span className="mono text-[10px] uppercase tracking-[0.2em] text-destructive border border-destructive/40 rounded px-1.5 py-0.5">Inactive</span>}
              </div>
              <div className="text-xs text-muted-foreground mono">{m.email}</div>
              {m.accepted_at && (
                <div className="text-[11px] text-muted-foreground mono mt-0.5">
                  Joined {new Date(m.accepted_at).toLocaleDateString()}
                </div>
              )}
            </div>
            {amOwner && m.role !== "owner" && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => toggleActive(m)} data-testid={`team-toggle-active-${m.id}`}>
                  {m.is_active ? <UserMinus className="h-3.5 w-3.5 mr-1" /> : <UserCheck className="h-3.5 w-3.5 mr-1" />}
                  {m.is_active ? "Deactivate" : "Reactivate"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDel(m)} className="text-destructive hover:text-destructive" data-testid={`team-remove-${m.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <>
          <h2 className="mt-8 text-lg font-extrabold tracking-tight">Pending invites</h2>
          <div className="mt-3 border border-border rounded-[8px] overflow-hidden" data-testid="team-invite-pending-list">
            {pending.map((iv) => (
              <div key={iv.id} className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-border bg-card"
                   data-testid={`team-invite-row-${iv.id}`}>
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{iv.name || iv.email}</div>
                  <div className="text-xs text-muted-foreground mono">{iv.email}</div>
                  <div className="text-[11px] text-muted-foreground mono mt-0.5">
                    Sent {new Date(iv.created_at).toLocaleDateString()} · expires {new Date(iv.expires_at).toLocaleTimeString()}
                  </div>
                </div>
                {amOwner && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => resend(iv.id)} data-testid={`team-resend-invite-${iv.id}`}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => cancelInvite(iv.id)} className="text-destructive hover:text-destructive" data-testid={`team-cancel-invite-${iv.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Invite form (owner-only) */}
      {amOwner && (
        <>
          <h2 className="mt-10 text-lg font-extrabold tracking-tight">Invite a teammate</h2>
          {overCap ? (
            <div className="mt-3 bg-red-50 border border-red-300 text-red-900 rounded-[8px] p-6 text-sm" data-testid="team-plan-limit-banner-red">
              <div className="mono text-[10px] uppercase tracking-widest font-bold">Over cap</div>
              <div className="mt-1">
                You&apos;re over your <strong>{plan_code}</strong> plan&apos;s teammate limit ({current_seat_count}/{seat_limit}). New invites blocked until you upgrade or remove a teammate.{" "}
                <Link to="/billing" className="text-accent font-semibold underline">Upgrade</Link>
              </div>
            </div>
          ) : atCap ? (
            <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-[8px] p-6 text-sm" data-testid="team-plan-limit-banner-amber">
              <div className="mono text-[10px] uppercase tracking-widest font-bold">At cap</div>
              <div className="mt-1">
                <strong>{plan_code}</strong> plan limit reached: {current_seat_count}/{seat_limit} teammates.{" "}
                <Link to="/billing" className="text-accent font-semibold underline">Upgrade to Pro</Link> to add more.
              </div>
            </div>
          ) : (
            <form onSubmit={invite} className="mt-3 bg-card border border-border rounded-[8px] p-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="team-invite-form">
              <div>
                <Label htmlFor="invite-email">Teammate email</Label>
                <Input id="invite-email" required type="email" placeholder="teammate@company.com"
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="team-invite-email" />
              </div>
              <div>
                <Label htmlFor="invite-name">Name (optional)</Label>
                <Input id="invite-name" placeholder="Jane Smith"
                       value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="team-invite-name" />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={submitting} className="btn-cta w-full" data-testid="team-invite-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Send invite
                </Button>
              </div>
            </form>
          )}
          {devInvite?.dev_token && (
            <div className="mt-3 border border-amber-500 bg-amber-50 text-amber-900 rounded-md p-3 text-xs" data-testid="team-invite-dev-token">
              <div className="font-bold mb-1 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Dev mode (email delivery not configured)</div>
              <div>Share this link with the teammate so they can set their password:</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="mono text-[11px] break-all flex-1">{devLink}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(devLink); toast.success("Link copied"); }}>
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {!amOwner && (
        <div className="mt-8 text-sm text-muted-foreground bg-secondary p-4 rounded-[8px]" data-testid="team-admin-readonly-notice">
          You're signed in as an admin. Only the owner can invite or remove teammates.
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="team-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove this teammate?</DialogTitle>
            <DialogDescription>
              {confirmDel?.name} ({confirmDel?.email}) will lose access immediately. This cannot be undone.
              Bookings they created stay in place.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove(confirmDel.id)} data-testid="team-remove-confirm">Remove teammate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
